import { useFormContext, Controller } from "react-hook-form";
import {
    Button,
    DialogActions,
    IconButton,
    InputAdornment,
    TextField,
    TextFieldProps,
    styled,
} from "@mui/material";
import { FC, forwardRef } from "react";
import { MobileDatePicker, MobileDatePickerProps } from "@mui/x-date-pickers";
import { Tooltip } from "@mui/material";
import Iconify from "../Iconify";
import { PickersActionBarProps } from "@mui/x-date-pickers/PickersActionBar";
import { useTranslation } from "react-i18next";

const StyledDialogActions = styled(DialogActions)({
    paddingTop: "0 !important",
});

const DatePickerActionBar = ({ onAccept, onCancel }: PickersActionBarProps) => {
    const { t } = useTranslation();
    return (
        <StyledDialogActions>
            <Button variant="text" color="primary" size="medium" onClick={onCancel}>
                {t("shared.confirm.cancel")}
            </Button>
            <Button variant="text" color="primary" size="medium" onClick={onAccept}>
                {t("shared.confirm.confirm")}
            </Button>
        </StyledDialogActions>
    );
};
interface IProps {
    name: string;
    tooltip?: string;
}

type RHFDatePickerProps = IProps &
    TextFieldProps &
    Omit<MobileDatePickerProps<Date, Date>, "renderInput" | "value" | "onChange">;

const WrappedDatePicker: FC<MobileDatePickerProps<Date, Date>> = ({ ...props }) => {
    return (
        <MobileDatePicker
            DialogProps={{
                sx: {
                    ".MuiDayPicker-slideTransition": {
                        minHeight: 230,
                    },
                },
            }}
            {...props}
        />
    );
};

const RHFTimePicker = forwardRef<HTMLInputElement, RHFDatePickerProps>(
    ({ name, tooltip, ...other }, ref) => {
        const { control, setValue, getValues } = useFormContext();

        return (
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => {
                    return (
                        <WrappedDatePicker
                            {...other}
                            disabled={!!other.disabled}
                            label={other.label}
                            showToolbar={false}
                            inputFormat="dd-MM-yyyy"
                            value={getValues(name)}
                            onChange={(newValue: Date | null) => {
                                setValue(name, newValue);
                            }}
                            components={{
                                ActionBar: DatePickerActionBar,
                            }}
                            renderInput={(params: any) => (
                                <>
                                    <TextField
                                        fullWidth
                                        helperText={error?.message}
                                        inputRef={ref}
                                        {...params}
                                        name={field.name}
                                        placeholder={other.placeholder}
                                        error={!!error}
                                        disabled={!!other.disabled}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {(tooltip ||
                                                        params.inputProps.value !== "") && (
                                                        <InputAdornment position="end">
                                                            {params.inputProps.value !== "" && (
                                                                <IconButton
                                                                    edge="end"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        setValue(name, "");
                                                                    }}
                                                                >
                                                                    <Iconify icon="eva:close-fill" />
                                                                </IconButton>
                                                            )}
                                                            {tooltip && (
                                                                <Tooltip title={tooltip || ""}>
                                                                    <IconButton edge="end">
                                                                        <Iconify icon="eva:info-fill" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </InputAdornment>
                                                    )}
                                                </>
                                            ),
                                        }}
                                    />
                                </>
                            )}
                        />
                    );
                }}
            />
        );
    }
);

export default RHFTimePicker;
